import { FieldValues, Path, PathValue, useFormContext } from 'react-hook-form';

import { HorizontalContainer, Select, Skeleton } from '@/components';
import {
    VetConsultationOutcomeEnum,
    VetConsultationReasonEnum,
    getEnumKeys,
    getEnumTranslationByKey,
} from '@/shared/enums';

import { R } from '@/caregiverSpace/resources/Texts.resx';

import { FormControl } from '@/caregiverSpace/shared/ui';

const outcomeOptions = getEnumKeys(VetConsultationOutcomeEnum).map((key) => {
    const value = VetConsultationOutcomeEnum[key];
    return {
        value,
        label: getEnumTranslationByKey(VetConsultationOutcomeEnum, value),
    };
});

const reasonOptions = getEnumKeys(VetConsultationReasonEnum).map((key) => {
    const value = VetConsultationReasonEnum[key];
    return {
        value,
        label: getEnumTranslationByKey(VetConsultationReasonEnum, value),
    };
});

type ReasonAndOutcomeSelectSectionProps<TFieldValues extends FieldValues> = {
    reasonFieldName: Path<TFieldValues>;
    outcomeFieldName: Path<TFieldValues>;
    isLoading: boolean;
};

export const ReasonAndOutcomeSelectSection = <
    TFieldValues extends FieldValues,
>({
    reasonFieldName,
    outcomeFieldName,
    isLoading,
}: ReasonAndOutcomeSelectSectionProps<TFieldValues>) => {
    const { register, setValue, formState } = useFormContext<TFieldValues>();
    const { errors } = formState;

    const handleSelectChange =
        (name: Path<TFieldValues>) =>
        (e: React.ChangeEvent<HTMLSelectElement>) => {
            const value = Number(e.target.value);
            setValue(name, value as PathValue<TFieldValues, typeof name>, {
                shouldDirty: true,
                shouldValidate: true,
            });
        };

    const renderSelect = (
        name: Path<TFieldValues>,
        label: string,
        options: { value: number; label: string }[],
        isLoading?: boolean,
    ) => {
        if (isLoading) {
            return (
                <FormControl label={label} error="" className="w-1/2">
                    <Skeleton className="h-14" />
                </FormControl>
            );
        }

        return (
            <FormControl
                label={label}
                error={errors[name]?.message as string}
                className="w-1/2"
            >
                <Select
                    {...register(name, {
                        required: R.Required,
                    })}
                    onChange={handleSelectChange(name)}
                    options={options}
                    prompt={label}
                />
            </FormControl>
        );
    };

    return (
        <HorizontalContainer spacing="md" className="justify-between">
            {renderSelect(
                reasonFieldName,
                R.ReasonForContact,
                reasonOptions,
                isLoading,
            )}

            {renderSelect(
                outcomeFieldName,
                R.Outcome,
                outcomeOptions,
                isLoading,
            )}
        </HorizontalContainer>
    );
};
